import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  X,
  User,
  GraduationCap,
  Award,
  BookOpen,
  CheckCircle2,
  RotateCw,
  Search,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import {
  getCertificateTypes,
  getCertificateTemplates,
  getStudentRecipients,
  getTeacherRecipients,
  issueCertificate,
} from "../../../../api/elearning/certificates/certificates";
import { getAllCoursesNoPaging } from "../../../../api/elearning/courses/courses";
import { getSubjectsLookup } from "../../../../api/elearning/subjects/subjectInfo";
import { toast } from "react-toastify";
import {
  CERT_DEFAULTS,
  computeExpiryDate,
  getLayoutDefaults,
} from "../certificateLayoutUtils";

const IssueCertificateModal = ({ isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [types, setTypes] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [recipientSearch, setRecipientSearch] = useState("");
  const [recipientListOpen, setRecipientListOpen] = useState(false);
  const recipientRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (recipientRef.current && !recipientRef.current.contains(e.target)) {
        setRecipientListOpen(false);
      }
    };
    if (recipientListOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [recipientListOpen]);

  const [form, setForm] = useState({
    recipient_type: "student",
    cert_type: "course_completion",
    template_id: "",
    recipient_id: "",
    recipient_name: "",
    course_id: "",
    subject_id: "",
    grade: "",
    issue_date: new Date().toISOString().split("T")[0],
    birth_year: "",
    organization_unit: CERT_DEFAULTS.organization_unit,
    issue_place: CERT_DEFAULTS.issue_place,
    expiry_date: "",
    validity_months: CERT_DEFAULTS.validity_months,
  });

  const fetchTypes = useCallback(async () => {
    try {
      const d = await getCertificateTypes();
      setTypes(d.types || []);
      if ((d.types || []).length > 0 && !form.cert_type) {
        setForm((f) => ({ ...f, cert_type: d.types[0].code }));
      }
    } catch {
      setTypes([]);
    }
  }, []);

  const fetchTemplates = useCallback(async (recipientType) => {
    try {
      const d = await getCertificateTemplates(recipientType);
      setTemplates(d.templates || []);
      setForm((f) => ({ ...f, template_id: (d.templates || [])[0]?.id || "" }));
    } catch {
      setTemplates([]);
    }
  }, []);

  const fetchRecipients = useCallback(async () => {
    if (form.recipient_type === "student") {
      try {
        const d = await getStudentRecipients(recipientSearch);
        setStudents(d.recipients || []);
      } catch {
        setStudents([]);
      }
    } else {
      try {
        const d = await getTeacherRecipients(recipientSearch);
        setTeachers(d.recipients || []);
      } catch {
        setTeachers([]);
      }
    }
  }, [form.recipient_type, recipientSearch]);

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      Promise.all([
        fetchTypes(),
        getAllCoursesNoPaging({ is_active: true }).then((r) =>
          setCourses(Array.isArray(r) ? r : (r?.records || r?.data || []))
        ),
        getSubjectsLookup({ status: "active" }).then((r) =>
          setSubjects(Array.isArray(r) ? r : (r?.records || r?.data || []))
        ),
      ]).finally(() => setLoading(false));
    }
  }, [isOpen, fetchTypes]);

  useEffect(() => {
    if (isOpen) fetchTemplates(form.recipient_type);
  }, [isOpen, form.recipient_type, fetchTemplates]);

  useEffect(() => {
    const t = setTimeout(fetchRecipients, 300);
    return () => clearTimeout(t);
  }, [form.recipient_type, recipientSearch, fetchRecipients]);

  const filteredTypes = types.filter(
    (t) =>
      t.recipient_type === form.recipient_type || t.recipient_type === "both"
  );
  const filteredTemplates = templates.filter(
    (t) =>
      t.recipient_type === form.recipient_type || t.recipient_type === "both"
  );
  const recipients = form.recipient_type === "student" ? students : teachers;

  const selectedTemplate = filteredTemplates.find((t) => t.id === form.template_id);
  const templateDefaults = getLayoutDefaults(selectedTemplate?.layout_config);

  useEffect(() => {
    if (!isOpen) return;
    setForm((f) => ({
      ...f,
      organization_unit:
        f.recipient_id && String(f.organization_unit || "").trim()
          ? f.organization_unit
          : templateDefaults.organization_unit,
      issue_place: templateDefaults.issue_place,
      validity_months: templateDefaults.validity_months,
      expiry_date: computeExpiryDate(f.issue_date, templateDefaults.validity_months) || "",
    }));
  }, [form.template_id, isOpen]);

  useEffect(() => {
    if (!form.issue_date) return;
    const months = form.validity_months ?? templateDefaults.validity_months;
    const exp = computeExpiryDate(form.issue_date, months);
    if (exp) {
      setForm((f) => (f.expiry_date === exp ? f : { ...f, expiry_date: exp }));
    }
  }, [form.issue_date, form.validity_months]);

  const handleRecipientSelect = (r) => {
    setForm((f) => ({
      ...f,
      recipient_id: r.id,
      recipient_name: r.name,
      birth_year: r.birth_year != null ? String(r.birth_year) : "",
      organization_unit:
        String(r.organization_unit || "").trim() || f.organization_unit || templateDefaults.organization_unit,
    }));
    setRecipientListOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.recipient_id) {
      toast.error("Vui lòng chọn người nhận chứng nhận.");
      return;
    }
    if (form.recipient_type === "student" && !String(form.grade || "").trim()) {
      toast.error("Vui lòng nhập Kết quả để in trên giấy chứng nhận.");
      return;
    }
    setSubmitting(true);
    try {
      await issueCertificate({
        template_id: form.template_id || undefined,
        cert_type: form.cert_type,
        recipient_type: form.recipient_type,
        recipient_id: form.recipient_id,
        course_id: form.course_id || undefined,
        subject_id: form.subject_id || undefined,
        grade: form.recipient_type === "student" ? form.grade : undefined,
        issue_date: form.issue_date,
        birth_year: form.birth_year ? parseInt(form.birth_year, 10) : undefined,
        organization_unit: form.organization_unit?.trim() || undefined,
        issue_place: form.issue_place?.trim() || undefined,
        expiry_date: form.expiry_date || undefined,
        validity_months: form.validity_months,
      });
      toast.success("Cấp chứng nhận thành công.");
      onSuccess?.();
      onClose();
      setForm({
        recipient_type: "student",
        cert_type: "course_completion",
        template_id: "",
        recipient_id: "",
        recipient_name: "",
        course_id: "",
        subject_id: "",
        grade: "",
        issue_date: new Date().toISOString().split("T")[0],
        birth_year: "",
        organization_unit: CERT_DEFAULTS.organization_unit,
        issue_place: CERT_DEFAULTS.issue_place,
        expiry_date: computeExpiryDate(new Date().toISOString().split("T")[0], CERT_DEFAULTS.validity_months),
        validity_months: CERT_DEFAULTS.validity_months,
      });
    } catch (err) {
      toast.error(err.response?.data?.message || "Không thể cấp chứng nhận.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white w-full max-w-2xl rounded-[2.5rem] shadow-xl overflow-hidden flex flex-col max-h-[90vh] relative z-[110]"
        >
          <div className="p-8 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
            <div>
              <h2 className="text-2xl font-black text-gray-800 uppercase tracking-tight">
                Cấp <span className="text-red-700">Chứng nhận</span>
              </h2>
              <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mt-1">
                Chọn loại chứng nhận, đối tượng và biểu mẫu
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full text-gray-400 transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-8 custom-scrollbar">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin w-10 h-10 border-2 border-red-600 border-t-transparent rounded-full" />
              </div>
            ) : (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest">
                      Đối tượng cấp
                    </label>
                    <div className="flex gap-2 p-1 bg-gray-100 rounded-2xl">
                      <button
                        type="button"
                        onClick={() =>
                          setForm((f) => ({
                            ...f,
                            recipient_type: "student",
                            recipient_id: "",
                            recipient_name: "",
                            organization_unit: templateDefaults.organization_unit,
                          }))
                        }
                        className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold transition-all ${
                          form.recipient_type === "student"
                            ? "bg-white text-red-700 shadow-sm"
                            : "text-gray-500"
                        }`}
                      >
                        <GraduationCap size={16} /> Học viên
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setForm((f) => ({
                            ...f,
                            recipient_type: "teacher",
                            recipient_id: "",
                            recipient_name: "",
                            organization_unit: templateDefaults.organization_unit,
                          }))
                        }
                        className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold transition-all ${
                          form.recipient_type === "teacher"
                            ? "bg-white text-red-700 shadow-sm"
                            : "text-gray-500"
                        }`}
                      >
                        <User size={16} /> Giáo viên
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest">
                      Loại chứng nhận
                    </label>
                    <select
                      value={form.cert_type}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, cert_type: e.target.value }))
                      }
                      className="w-full bg-gray-50 border border-gray-200 py-3 px-4 rounded-2xl text-sm font-bold outline-none focus:border-red-500"
                    >
                      {filteredTypes.map((t) => (
                        <option key={t.id} value={t.code}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest">
                    Biểu mẫu giấy chứng nhận
                  </label>
                  <select
                    value={form.template_id}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, template_id: e.target.value }))
                    }
                    className="w-full bg-gray-50 border border-gray-200 py-3 px-4 rounded-2xl text-sm font-bold outline-none focus:border-red-500"
                  >
                    <option value="">-- Mặc định --</option>
                    {filteredTemplates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2" ref={recipientRef}>
                  <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest">
                    {form.recipient_type === "student"
                      ? "Chọn học viên"
                      : "Chọn giáo viên"}
                  </label>
                  <div className="relative">
                    <Search
                      size={18}
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                    />
                    <input
                      type="text"
                      placeholder="Tìm kiếm theo tên..."
                      value={recipientSearch}
                      onChange={(e) => setRecipientSearch(e.target.value)}
                      onFocus={() => setRecipientListOpen(true)}
                      className="w-full bg-gray-50 border border-gray-200 pl-12 pr-4 py-3 rounded-2xl text-sm font-bold outline-none focus:border-red-500"
                    />
                    {form.recipient_name && (
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-bold text-red-600">
                        {form.recipient_name}
                      </span>
                    )}
                    <AnimatePresence>
                      {recipientListOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: -8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -8 }}
                          className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-2xl shadow-lg max-h-48 overflow-y-auto z-20"
                        >
                          {recipients.map((r) => (
                            <button
                              key={r.id}
                              type="button"
                              onClick={() => handleRecipientSelect(r)}
                              className="w-full px-4 py-3 text-left text-sm font-medium hover:bg-red-50 transition-colors flex items-center gap-2"
                            >
                              <User size={14} className="text-gray-400" />
                              {r.name}
                              {r.phone && (
                                <span className="text-gray-400 text-xs">
                                  {r.phone}
                                </span>
                              )}
                            </button>
                          ))}
                          {recipients.length === 0 && (
                            <div className="px-4 py-6 text-center text-gray-400 text-sm">
                              Không tìm thấy
                            </div>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>

                {form.recipient_type === "student" && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest">
                        Khóa học (tùy chọn)
                      </label>
                      <select
                        value={form.course_id}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, course_id: e.target.value }))
                        }
                        className="w-full bg-gray-50 border border-gray-200 py-3 px-4 rounded-2xl text-sm font-bold outline-none focus:border-red-500"
                      >
                        <option value="">-- Chọn --</option>
                        {courses.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest">
                        Môn học (tùy chọn)
                      </label>
                      <select
                        value={form.subject_id}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            subject_id: e.target.value,
                          }))
                        }
                        className="w-full bg-gray-50 border border-gray-200 py-3 px-4 rounded-2xl text-sm font-bold outline-none focus:border-red-500"
                      >
                        <option value="">-- Chọn --</option>
                        {subjects.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest">
                        Kết quả
                      </label>
                      <input
                        type="text"
                        value={form.grade}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, grade: e.target.value }))
                        }
                        placeholder="VD: Hoàn thành khóa học KNS cấp tiểu học"
                        className="w-full bg-gray-50 border border-gray-200 py-3 px-4 rounded-2xl text-sm font-bold outline-none focus:border-red-500"
                      />
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest">
                      Năm sinh
                    </label>
                    <input
                      type="number"
                      min={1950}
                      max={2100}
                      placeholder="VD: 1990"
                      value={form.birth_year}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, birth_year: e.target.value }))
                      }
                      className="w-full bg-gray-50 border border-gray-200 py-3 px-4 rounded-2xl text-sm font-bold outline-none focus:border-red-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest">
                      Đơn vị
                    </label>
                    <input
                      type="text"
                      value={form.organization_unit}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, organization_unit: e.target.value }))
                      }
                      className="w-full bg-gray-50 border border-gray-200 py-3 px-4 rounded-2xl text-sm font-bold outline-none focus:border-red-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest">
                      Nơi cấp
                    </label>
                    <input
                      type="text"
                      value={form.issue_place}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, issue_place: e.target.value }))
                      }
                      className="w-full bg-gray-50 border border-gray-200 py-3 px-4 rounded-2xl text-sm font-bold outline-none focus:border-red-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest">
                      Ngày cấp
                    </label>
                    <input
                      type="date"
                      value={form.issue_date}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, issue_date: e.target.value }))
                      }
                      className="w-full bg-gray-50 border border-gray-200 py-3 px-4 rounded-2xl text-sm font-bold outline-none focus:border-red-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest">
                      Hiệu lực (tháng)
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={60}
                      value={form.validity_months}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          validity_months: Number(e.target.value) || 6,
                        }))
                      }
                      className="w-full bg-gray-50 border border-gray-200 py-3 px-4 rounded-2xl text-sm font-bold outline-none focus:border-red-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest">
                      Hết hiệu lực
                    </label>
                    <input
                      type="date"
                      value={form.expiry_date || ""}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, expiry_date: e.target.value }))
                      }
                      className="w-full bg-gray-50 border border-gray-200 py-3 px-4 rounded-2xl text-sm font-bold outline-none focus:border-red-500"
                    />
                  </div>
                </div>
              </div>
            )}
          </form>

          <div className="p-8 border-t border-gray-50 bg-gray-50/30 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 rounded-xl font-bold text-gray-500 hover:bg-gray-100 transition-all uppercase text-xs tracking-wider"
            >
              Hủy
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={submitting || loading || !form.recipient_id}
              className="px-8 py-3 bg-red-700 hover:bg-red-800 text-white font-bold rounded-xl flex items-center gap-2 shadow-lg transition-all active:scale-95 uppercase text-xs tracking-wider disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <RotateCw size={18} className="animate-spin" />
              ) : (
                <CheckCircle2 size={18} />
              )}
              {submitting ? "Đang xử lý..." : "XÁC NHẬN CẤP"}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default IssueCertificateModal;
